import { memo, type ComponentType } from 'react';
import { Handle, Position, type NodeProps } from 'reactflow';
import {
  Bot,
  BookOpen,
  Braces,
  Brain,
  FileOutput,
  FileSearch,
  GitBranch,
  Globe,
  GraduationCap,
  LibraryBig,
  Network,
  PencilLine,
  ShieldCheck,
  Sparkles,
  UserRound,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { CollabNodeRole } from './collabTypes';

type CollabFlowNodeData = {
  role: CollabNodeRole;
  title: string;
  subtitle?: string;
};

const roleIcon: Record<CollabNodeRole, ComponentType<{ className?: string }>> = {

  start: UserRound,
  drafter: PencilLine,
  editor: Bot,
  output: FileOutput,
  orchestrator: Sparkles,
  planner: Brain,
  web_research: Globe,
  academic_research: GraduationCap,
  code_analysis: Braces,
  source_verification: ShieldCheck,
  timeline_builder: GitBranch,
  citation_manager: LibraryBig,
  report_writer: FileSearch,
  knowledge_graph: Network,
};

const roleBadgeClassName: Record<CollabNodeRole, string> = {
  start: 'bg-slate-100 text-slate-700 border-slate-300',
  drafter: 'bg-sky-100 text-sky-700 border-sky-300',
  editor: 'bg-violet-100 text-violet-700 border-violet-300',
  output: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  orchestrator: 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-300',
  planner: 'bg-indigo-100 text-indigo-700 border-indigo-300',
  web_research: 'bg-cyan-100 text-cyan-700 border-cyan-300',
  academic_research: 'bg-blue-100 text-blue-700 border-blue-300',
  code_analysis: 'bg-amber-100 text-amber-700 border-amber-300',
  source_verification: 'bg-rose-100 text-rose-700 border-rose-300',
  timeline_builder: 'bg-orange-100 text-orange-700 border-orange-300',
  citation_manager: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  report_writer: 'bg-violet-100 text-violet-700 border-violet-300',
  knowledge_graph: 'bg-teal-100 text-teal-700 border-teal-300',
};

const roleIconClassName: Record<CollabNodeRole, string> = {
  start: 'text-slate-700',
  drafter: 'text-sky-600',
  editor: 'text-violet-600',
  output: 'text-emerald-600',
  orchestrator: 'text-fuchsia-600',
  planner: 'text-indigo-600',
  web_research: 'text-cyan-600',
  academic_research: 'text-blue-600',
  code_analysis: 'text-amber-600',
  source_verification: 'text-rose-600',
  timeline_builder: 'text-orange-600',
  citation_manager: 'text-emerald-600',
  report_writer: 'text-violet-600',
  knowledge_graph: 'text-teal-600',
};

function CollabFlowNode({ data, selected }: NodeProps<CollabFlowNodeData>) {
  const Icon = roleIcon[data.role];

  return (
    <div
      className={`min-w-[190px] rounded-xl border bg-background px-4 py-3 shadow-sm transition ${
        selected ? 'border-primary ring-2 ring-primary/20' : 'border-border'
      }`}
    >
      {data.role !== 'start' ? <Handle type="target" position={Position.Left} className="!bg-primary" /> : null}
      {data.role !== 'output' ? <Handle type="source" position={Position.Right} className="!bg-primary" /> : null}
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-muted p-2">
          <Icon className={`h-4 w-4 ${roleIconClassName[data.role]}`} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold">{data.title}</span>
            {data.role === 'editor' || data.role === 'orchestrator' ? <BookOpen className="h-3.5 w-3.5 text-muted-foreground" /> : null}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{data.subtitle || 'Click to configure'}</p>
          <Badge variant="outline" className={`mt-3 ${roleBadgeClassName[data.role]}`}>
            {data.role.replace(/_/g, ' ')}
          </Badge>
        </div>
      </div>
    </div>
  );
}

export default memo(CollabFlowNode);
